import { observer } from 'mobx-react-lite'
import { useWatch, type UseFormReturn } from 'react-hook-form'
import { type AssignmentFormValues } from '../schemas'
import { useTranslationTyped } from 'i18n/i18n'
import { type SlideDeckRole } from '@breakoutlearning/firebase-repository/models/SlideDeckRole'
import { RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import { useEffect } from 'react'
import { BreakoutRangeSlider } from 'components/design-system/BreakoutRangeSlider'
import { BreakoutInfoTooltip } from 'components/design-system/BreakoutPrefabTooltips'
import { TooltipWrapper } from 'pages/instructor/shared/GradingTooltips'

export const GroupingSizeInput = observer(function GroupingSizeInput({
  disableGroupingReason,
  readOnly,
  form,
  minGroupSize,
  maxGroupSize,
  roleGroupSizeConstraints,
  roles,
}: {
  disableGroupingReason: string | undefined
  readOnly: boolean
  form: UseFormReturn<AssignmentFormValues>
  minGroupSize: number
  maxGroupSize: number
  roleGroupSizeConstraints:
    | { minGroupSize: number; maxGroupSize: number }
    | null
    | undefined
  roles?: SlideDeckRole[]
}) {
  const { tt } = useTranslationTyped()

  const hasRoles = !!roles && roles.length > 0
  const fixedGroupSize = minGroupSize === maxGroupSize
  const showSlider = !(hasRoles && fixedGroupSize)

  // useWatch (not form.watch) is required because observer's memo blocks the form-root re-render cascade.
  const minValueRaw = useWatch({
    control: form.control,
    name: 'groupingSizeMinimum',
  })
  const maxValueRaw = useWatch({ control: form.control, name: 'groupingSize' })

  useEffect(() => {
    if (!roleGroupSizeConstraints?.maxGroupSize) return
    const { maxGroupSize } = roleGroupSizeConstraints
    const currentGroupingSize = form.getValues('groupingSize')
    const currentGroupingSizeMinimum = form.getValues('groupingSizeMinimum')

    // set the role constraints to the form for form validation purposes
    form.setValue('roleGroupSizeConstraints', roleGroupSizeConstraints, {
      shouldDirty: false,
    })

    // on mount, if cubit current (default) grouping size is > maxGroupSize || groupingSize < minGroupSize, set to maxGroupSize
    if (currentGroupingSize !== undefined) {
      if (maxGroupSize < currentGroupingSize)
        form.setValue('groupingSize', maxGroupSize, { shouldDirty: false })
      else if (currentGroupingSize < minGroupSize)
        form.setValue('groupingSize', minGroupSize, { shouldDirty: false })
    }

    // Seed the minimum group size with the role-imposed floor when none is
    // saved yet — and clamp existing values that fall below the floor (e.g.
    // a brand-new assignment defaults to groupingSizeMinimum=1, but the role
    // requires 3). Without this clamp the slider opens with a validation
    // error the user has to dismiss by hand.
    if (
      currentGroupingSizeMinimum === undefined ||
      currentGroupingSizeMinimum < minGroupSize
    ) {
      form.setValue('groupingSizeMinimum', minGroupSize, { shouldDirty: false })
    }

    form.trigger('groupingSize')
    form.trigger('groupingSizeMinimum')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trackMin = 1
  const trackMax = RoomState.maxAllowedUsers
  const lo = clamp(minValueRaw ?? minGroupSize, trackMin, trackMax)
  const hi = clamp(maxValueRaw ?? maxGroupSize, trackMin, trackMax)

  const handleChange = ([nextLo, nextHi]: [number, number]) => {
    if (nextLo !== minValueRaw) {
      form.setValue('groupingSizeMinimum', nextLo, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
    if (nextHi !== maxValueRaw) {
      form.setValue('groupingSize', nextHi, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  const minError = form.formState.errors.groupingSizeMinimum
  const maxError = form.formState.errors.groupingSize
  const error = minError ?? maxError

  const isDisabled = !!disableGroupingReason || readOnly
  const description = (() => {
    type Variant = 'lockedSingle' | 'lockedRange' | 'roleBounds' | 'standard'
    const variant: Variant = isDisabled
      ? lo === hi
        ? 'lockedSingle'
        : 'lockedRange'
      : hasRoles
        ? 'roleBounds'
        : 'standard'

    switch (variant) {
      case 'lockedSingle':
        return tt.instructor_library.group_size_locked_single_description({
          groupSize: lo,
        })
      case 'lockedRange':
        return tt.instructor_library.group_size_locked_range_description({
          minSize: lo,
          maxSize: hi,
        })
      case 'roleBounds':
        return tt.instructor_library.group_size_range_description_with_role_bounds(
          { minGroupSize, maxGroupSize }
        )
      case 'standard':
        return tt.instructor_library.group_size_range_description()
    }
  })()

  const readOnlyReason = isDisabled
    ? (disableGroupingReason ??
      tt.instructor_library.group_size_read_only_generic_reason())
    : undefined

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center gap-1">
        <h3 className="text-title-medium">
          {tt.instructor_library.group_size_title()}
        </h3>
        <BreakoutInfoTooltip
          placement="bottom"
          content={
            <TooltipWrapper className="max-w-[330px]">
              {tt.instructor_library.group_size_tooltip()}
            </TooltipWrapper>
          }
        />
      </div>
      {showSlider && (
        <BreakoutRangeSlider
          testId="group-size-range-slider"
          name="groupingSizeRange"
          min={trackMin}
          max={trackMax}
          value={[lo, hi]}
          onChange={handleChange}
          effectiveMin={hasRoles ? minGroupSize : trackMin}
          effectiveMax={hasRoles ? maxGroupSize : trackMax}
          disabled={isDisabled}
          minLabelFormatter={(n) =>
            tt.instructor_library.group_size_min_pill_label({ n })
          }
          maxLabelFormatter={(n) =>
            lo === hi
              ? tt.instructor_library.group_size_only_pill_label({ n })
              : tt.instructor_library.group_size_max_pill_label({ n })
          }
          minThumbAriaLabel={tt.instructor_library.group_size_min_thumb_aria_label()}
          maxThumbAriaLabel={tt.instructor_library.group_size_max_thumb_aria_label()}
          description={description}
          readOnlyLabel={readOnlyReason}
          error={error}
        />
      )}
      {hasRoles && roles && (
        <RolesDescriptionBox
          minGroupSize={minGroupSize}
          maxGroupSize={maxGroupSize}
          roles={roles}
        />
      )}
    </div>
  )
})

const clamp = (n: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, n))

const RolesDescriptionBox = ({
  minGroupSize,
  maxGroupSize,
  roles,
}: {
  minGroupSize: number
  maxGroupSize: number
  roles: SlideDeckRole[]
}) => {
  const { tt } = useTranslationTyped()
  const fixedGroupSize = minGroupSize === maxGroupSize

  return (
    <div
      data-testid="roles-description-box"
      className="flex flex-1 flex-col gap-3 rounded-lg border border-fixed-accent-color bg-fixed-eggshell px-3 py-2 text-body-large"
    >
      <div>
        <h4 className="font-bold">
          {tt.instructor_library.roleplay_requirements()}
        </h4>
        <ul className="list-disc pl-5">
          {roles.map((role) => {
            const roleReqDisplay =
              role.data.roleMin === role.data.roleMax
                ? role.data.roleMin
                : `${role.data.roleMin}–${role.data.roleMax}`

            return (
              <li key={role.id}>
                {tt.instructor_library.role_min_x_name({
                  roleMin: roleReqDisplay,
                  roleName: role.data.roleName,
                })}
              </li>
            )
          })}
        </ul>
      </div>
      <span>
        {fixedGroupSize &&
          tt.instructor_library.role_fixed_group_size_description({
            groupSize: minGroupSize,
          })}
        {!fixedGroupSize &&
          tt.instructor_library.role_range_group_size_description({
            minGroupSize,
            maxGroupSize,
          })}
      </span>
    </div>
  )
}
